/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';

import {
   createDynamicColumnsWidth,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   JustifyContent,
} from '@dreryk/react-components';

import { magistralDrugTranslationKeys } from '../../../../../../translations';
import { createInitialMedicineRawMaterial } from '../../../../magistralDrug/create/MagistralDrugCreateForm.hooks';
import {
   MagistralDrugCreateFormBean,
   MagistralDrugCreateFormController,
} from '../../../../magistralDrug/create/MagistralDrugCreateForm.types';

import { ExcessiveCheckboxCell } from './ExcessiveCheckboxCell';
import { MedicineRawMaterialNameAutocompleteCell } from './MedicineRawMaterialNameAutocompleteCell';
import { MedicineRawMaterialTableBean } from './MedicineRawMaterialTable.types';
import { createMedicineRawMaterialTableHeaderWithTooltip } from './MedicineRawMaterialTableHeader';
import { QuantityAbbreviationSelectCell } from './QuantityAbbreviationSelectCell';
import { QuantityNumberInputCell } from './QuantityNumberInputCell';
import { RpwCheckboxCell } from './RpwCheckboxCell';
import { UnitSelectCell } from './UnitSelectCell';

const useMedicineRawMaterialColumns = (): InteractiveTableColumnConfiguration<MedicineRawMaterialTableBean>[] => {
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         name: '100%',
         rpw: '48px',
         quantityAbbreviation: '100px',
         quantity: '110px',
         unit: '72px',
         excessive: '40px',
         options: '24px',
      });

      return [
         {
            name: 'name',
            label: magistralDrugTranslationKeys.medicineRawMaterials.columns.name,
            width: columnWidths.name,

            renderer: ({ bean, index }) => (
               <MedicineRawMaterialNameAutocompleteCell
                  testId={'name'}
                  name={'name'}
                  index={index}
                  defaultValue={bean.name}
               />
            ),
         },
         {
            name: 'quantityAbbreviation',
            headerRenderer: createMedicineRawMaterialTableHeaderWithTooltip(
               magistralDrugTranslationKeys.medicineRawMaterials.columns.quantityAbbreviation,
               magistralDrugTranslationKeys.medicineRawMaterials.columns.headerTooltips.quantityAbbr
            ),
            width: columnWidths.quantityAbbreviation,

            renderer: ({ bean, index }) => (
               <QuantityAbbreviationSelectCell
                  testId={'quantityAbbreviation'}
                  name={'quantityAbbreviation'}
                  index={index}
                  defaultValue={bean.quantityAbbreviation}
               />
            ),
         },
         {
            name: 'quantity',
            label: magistralDrugTranslationKeys.medicineRawMaterials.columns.quantity,
            width: columnWidths.quantity,

            renderer: ({ index, bean }) => (
               <QuantityNumberInputCell
                  testId={'quantity'}
                  name={'quantity'}
                  index={index}
                  defaultValue={bean.quantity}
               />
            ),
         },
         {
            name: 'unit',
            label: magistralDrugTranslationKeys.medicineRawMaterials.columns.unit,
            width: columnWidths.unit,

            renderer: ({ bean, index }) => (
               <UnitSelectCell testId={'unit'} name={'unit'} index={index} defaultValue={bean.unit} />
            ),
         },
         {
            name: 'rpw',
            headerRenderer: createMedicineRawMaterialTableHeaderWithTooltip(
               magistralDrugTranslationKeys.medicineRawMaterials.columns.rpw,
               magistralDrugTranslationKeys.medicineRawMaterials.columns.headerTooltips.narcoticSubstance
            ),
            width: columnWidths.rpw,
            justifyContent: JustifyContent.CENTER,

            renderer: ({ bean, index }) => (
               <RpwCheckboxCell testId={'rpw'} name={'rpw'} index={index} defaultValue={bean.rpw} />
            ),
         },
         {
            name: 'excessive',
            headerRenderer: createMedicineRawMaterialTableHeaderWithTooltip(
               magistralDrugTranslationKeys.medicineRawMaterials.columns.excessive,
               magistralDrugTranslationKeys.medicineRawMaterials.columns.headerTooltips.explicitOverdose
            ),
            width: columnWidths.excessive,

            renderer: ({ index, bean }) => (
               <ExcessiveCheckboxCell
                  testId={'excessive'}
                  name={'excessive'}
                  index={index}
                  defaultValue={bean.excessive}
               />
            ),
         },
         {
            name: 'options',
            width: columnWidths.options,

            renderer: ({ index, rowsCount }) => {
               if (rowsCount === 1) {
                  return null;
               }

               return <InteractiveTable.DeleteRowCell index={index} />;
            },
         },
      ];
   }, []);
};

export type MedicineRawMaterialTableProps = Pick<MagistralDrugCreateFormController, 'register'>;

export const MedicineRawMaterialTable = ({ register }: MedicineRawMaterialTableProps): JSX.Element => {
   const columns = useMedicineRawMaterialColumns();

   return (
      <InteractiveTable<MedicineRawMaterialTableBean, MagistralDrugCreateFormBean>
         testId={'medicineRawMaterials'}
         name={'medicineRawMaterials'}
         register={register}
         columns={columns}
         minBodyWidth={'530px'}
      >
         <InteractiveTable.AddRowButton
            label={magistralDrugTranslationKeys.medicineRawMaterials.addMaterial}
            onAddRow={createInitialMedicineRawMaterial}
         />
      </InteractiveTable>
   );
};
